/**
 * TruWe page builder — block model.
 *
 * One shared definition drives three things:
 *  - what the storefront renders (BlockRenderer)
 *  - what the inline editor shows as a form (FIELD_SCHEMA)
 *  - what the server accepts (sanitizeDocument)
 *
 * Keep this file free of React and of server-only imports: it is used on both
 * sides of the wire.
 */

export type BlockType =
  | "hero"
  | "richText"
  | "image"
  | "features"
  | "faq"
  | "cta"
  | "products"
  | "stats"
  | "quote"
  | "spacer"
  | "navLinks";


export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export type BlockData = { [key: string]: Json };

export type Block = {
  id: string;
  type: BlockType;
  data: BlockData;
};

export type PageDocument = {
  blocks: Block[];
};

export type PageSeo = {
  title?: string;
  description?: string;
  image?: string;
  noindex?: boolean;
};

/* ------------------------------------------------------------------ */
/* Field schema                                                        */
/* ------------------------------------------------------------------ */

export type FieldKind =
  | "text"
  | "textarea"
  | "image"
  | "url"
  | "select"
  | "list"
  | "product";

export type Field = {
  key: string;
  label: string;
  kind: FieldKind;
  placeholder?: string;
  /** Plain-language guidance shown under the control. */
  help?: string;
  options?: { value: string; label: string }[];
  /** For kind: "list" — the shape of each row. */
  itemFields?: Field[];
  itemLabel?: string;
  max?: number;
  /** Tucked away under "Advanced settings" in the editor. */
  advanced?: boolean;
};


export type BlockDef = {
  type: BlockType;
  label: string;
  description: string;
  fields: Field[];
  defaults: BlockData;
};

const ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Centre" },
];

export const BLOCK_DEFS: BlockDef[] = [
  {
    type: "hero",
    label: "Hero",
    description: "Big opening statement with an optional picture and button.",
    fields: [
      { key: "eyebrow", label: "Small label above the title", kind: "text" },
      { key: "title", label: "Title", kind: "text" },
      { key: "body", label: "Supporting text", kind: "textarea" },
      { key: "image", label: "Picture", kind: "image", help: "Wide pictures look best — about 1600 x 1000 pixels." },
      { key: "ctaLabel", label: "Button text", kind: "text" },
      { key: "ctaHref", label: "Button link", kind: "url", placeholder: "/shop" },
      { key: "align", label: "Alignment", kind: "select", options: ALIGN_OPTIONS, advanced: true },
    ],
    defaults: {
      eyebrow: "",
      title: "A headline that sells",
      body: "One or two lines that explain the promise.",
      image: "",
      ctaLabel: "Shop now",
      ctaHref: "/shop",
      align: "center",
    },
  },
  {
    type: "richText",
    label: "Text section",
    description: "A heading and paragraphs. Blank lines start a new paragraph.",
    fields: [
      { key: "title", label: "Heading", kind: "text" },
      { key: "body", label: "Text", kind: "textarea" },
      { key: "align", label: "Alignment", kind: "select", options: ALIGN_OPTIONS, advanced: true },
    ],
    defaults: { title: "Section heading", body: "Write your copy here.", align: "left" },
  },
  {
    type: "image",
    label: "Picture",
    description: "A full-width picture with an optional caption.",
    fields: [
      { key: "image", label: "Picture", kind: "image", help: "Wide pictures look best — about 1600 x 1000 pixels." },
      { key: "alt", label: "Describe the picture (for accessibility)", kind: "text" },
      { key: "caption", label: "Caption", kind: "text" },
      {
        key: "width",
        label: "Width",
        kind: "select",
        options: [
          { value: "wide", label: "Contained" },
          { value: "full", label: "Edge to edge" },
        ],
      },
    ],
    defaults: { image: "", alt: "", caption: "", width: "wide" },
  },
  {
    type: "features",
    label: "Feature grid",
    description: "Three-up grid of short benefit cards.",
    fields: [
      { key: "title", label: "Heading", kind: "text" },
      {
        key: "items",
        label: "Cards",
        kind: "list",
        itemLabel: "Card",
        max: 12,
        itemFields: [
          { key: "title", label: "Card title", kind: "text" },
          { key: "body", label: "Card text", kind: "textarea" },
        ],
      },
    ],
    defaults: {
      title: "Why it works",
      items: [
        { title: "Research-backed doses", body: "Every active at a level studied in trials." },
        { title: "Absorption first", body: "Bio-Absorb Matrix delivery support." },
        { title: "Lab tested", body: "Third-party tested on every batch." },
      ],
    },
  },
  {
    type: "faq",
    label: "Questions & answers",
    description: "Expandable list of common questions.",
    fields: [
      { key: "title", label: "Heading", kind: "text" },
      {
        key: "items",
        label: "Questions",
        kind: "list",
        itemLabel: "Question",
        max: 24,
        itemFields: [
          { key: "q", label: "Question", kind: "text" },
          { key: "a", label: "Answer", kind: "textarea" },
        ],
      },
    ],
    defaults: {
      title: "Questions",
      items: [{ q: "How long until I feel a difference?", a: "Most people report changes in 2–4 weeks." }],
    },
  },
  {
    type: "cta",
    label: "Call to action",
    description: "A closing band with a button.",
    fields: [
      { key: "title", label: "Title", kind: "text" },
      { key: "body", label: "Supporting text", kind: "textarea" },
      { key: "ctaLabel", label: "Button text", kind: "text" },
      { key: "ctaHref", label: "Button link", kind: "url" },
    ],
    defaults: {
      title: "Start your protocol",
      body: "Free delivery over ₹999.",
      ctaLabel: "Shop now",
      ctaHref: "/shop",
    },
  },
  {
    type: "products",
    label: "Product cards",
    description: "Link cards to product pages.",
    fields: [
      { key: "title", label: "Heading", kind: "text" },
      {
        key: "items",
        label: "Products",
        kind: "list",
        itemLabel: "Product",
        max: 8,
        itemFields: [
          { key: "handle", label: "Product", kind: "product" },
          { key: "title", label: "Card title (optional)", kind: "text" },
          { key: "body", label: "Card text (optional)", kind: "textarea" },
          { key: "image", label: "Picture (optional)", kind: "image" },
        ],
      },
    ],
    defaults: {
      title: "Shop the range",
      items: [{ handle: "mag-absorb-pro", title: "", body: "", image: "" }],
    },
  },
  {
    type: "stats",
    label: "Numbers strip",
    description: "A row of headline numbers.",
    fields: [
      {
        key: "items",
        label: "Numbers",
        kind: "list",
        itemLabel: "Number",
        max: 6,
        itemFields: [
          { key: "value", label: "Number", kind: "text", placeholder: "98%" },
          { key: "label", label: "Label", kind: "text" },
        ],
      },
    ],
    defaults: {
      items: [
        { value: "3rd party", label: "Batch tested" },
        { value: "GMP", label: "Certified facility" },
        { value: "FSSAI", label: "Licensed" },
      ],
    },
  },
  {
    type: "quote",
    label: "Quote",
    description: "A pulled quote or customer line.",
    fields: [
      { key: "body", label: "Quote", kind: "textarea" },
      { key: "author", label: "Attribution", kind: "text" },
    ],
    defaults: { body: "Sleep finally feels normal again.", author: "Verified customer" },
  },
  {
    type: "spacer",
    label: "Spacing",
    description: "Adds vertical breathing room.",
    fields: [
      {
        key: "size",
        label: "Size",
        kind: "select",
        options: [
          { value: "sm", label: "Small" },
          { value: "md", label: "Medium" },
          { value: "lg", label: "Large" },
        ],
      },
    ],
    defaults: { size: "md" },
  },
  {
    type: "navLinks",
    label: "Menu links",
    description: "The links shown in the website menu.",
    fields: [
      {
        key: "items",
        label: "Menu links",
        kind: "list",
        itemLabel: "Link",
        max: 10,
        itemFields: [
          { key: "label", label: "Link text", kind: "text", placeholder: "Shop" },
          { key: "href", label: "Goes to", kind: "url", placeholder: "/shop" },
        ],
      },
    ],
    defaults: {
      items: [
        { label: "Shop", href: "/shop" },
        { label: "Library", href: "/blog" },
      ],
    },
  },
];

export const BLOCK_DEF_BY_TYPE: Record<string, BlockDef> = Object.fromEntries(
  BLOCK_DEFS.map((d) => [d.type, d]),
);

export function newBlock(type: BlockType): Block {
  const def = BLOCK_DEF_BY_TYPE[type];
  return {
    id: `b_${Math.random().toString(36).slice(2, 10)}`,
    type,
    data: JSON.parse(JSON.stringify(def?.defaults ?? {})) as BlockData,
  };
}

/* ------------------------------------------------------------------ */
/* Sanitising                                                          */
/* ------------------------------------------------------------------ */

const MAX_BLOCKS = 60;
const MAX_TEXT = 4000;

function str(v: unknown, max = MAX_TEXT): string {
  if (typeof v !== "string") return "";
  return v.slice(0, max);
}

/** Blocks links to javascript:/data: schemes; allows paths and http(s). */
export function safeHref(v: unknown): string {
  const raw = str(v, 600).trim();
  if (!raw) return "";
  if (/^(https?:\/\/|\/|#|mailto:|tel:)/i.test(raw)) return raw;
  return "";
}

export function safeImage(v: unknown): string {
  const raw = str(v, 1200).trim();
  if (!raw) return "";
  if (/^(https?:\/\/|\/)/i.test(raw)) return raw;
  return "";
}

function sanitizeField(field: Field, value: unknown): unknown {
  switch (field.kind) {
    case "url":
      return safeHref(value);
    case "product":
      return str(value, 80).trim().toLowerCase();
    case "image":
      return safeImage(value);
    case "select": {
      const allowed = (field.options ?? []).map((o) => o.value);
      const v = str(value, 40);
      return allowed.includes(v) ? v : (allowed[0] ?? "");
    }
    case "list": {
      const rows = Array.isArray(value) ? value : [];
      const max = field.max ?? 12;
      return rows.slice(0, max).map((row) => {
        const obj = (row ?? {}) as Record<string, unknown>;
        const out: Record<string, Json> = {};
        for (const f of field.itemFields ?? []) out[f.key] = sanitizeField(f, obj[f.key]) as Json;
        return out;
      });
    }
    default:
      return str(value);
  }
}

export function sanitizeBlock(input: unknown): Block | null {
  const raw = (input ?? {}) as { id?: unknown; type?: unknown; data?: unknown };
  const def = BLOCK_DEF_BY_TYPE[str(raw.type, 40)];
  if (!def) return null;
  const data = (raw.data ?? {}) as Record<string, unknown>;
  const out: BlockData = {};
  for (const f of def.fields) out[f.key] = sanitizeField(f, data[f.key]) as Json;
  const id = str(raw.id, 40) || `b_${Math.random().toString(36).slice(2, 10)}`;
  return { id, type: def.type, data: out };
}

export function sanitizeDocument(input: unknown): PageDocument {
  const raw = (input ?? {}) as { blocks?: unknown };
  const list = Array.isArray(raw.blocks) ? raw.blocks : [];
  const blocks: Block[] = [];
  for (const b of list.slice(0, MAX_BLOCKS)) {
    const clean = sanitizeBlock(b);
    if (clean) blocks.push(clean);
  }
  return { blocks };
}

export function sanitizeSeo(input: unknown): PageSeo {
  const raw = (input ?? {}) as Record<string, unknown>;
  return {
    title: str(raw["title"], 160),
    description: str(raw["description"], 320),
    image: safeImage(raw["image"]),
    noindex: raw["noindex"] === true,
  };
}

/**
 * Normalises a page path. Custom pages live under /pages/<slug>; named
 * regions inside existing routes use the `region:<id>` form.
 */
export function normalizePath(input: unknown): string {
  const raw = String(input ?? "").trim().toLowerCase();
  if (raw.startsWith("region:")) {
    const id = raw.slice(7).replace(/[^a-z0-9-]/g, "");
    return id ? `region:${id}` : "";
  }
  const slug = raw
    .replace(/^\/+|\/+$/g, "")
    .replace(/^pages\//, "")
    .replace(/[^a-z0-9/-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return slug ? `/pages/${slug}` : "";
}
